import { useEffect, useState } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export type AuthState =
  | { state: 'loading' }
  | { state: 'unavailable' }
  | { state: 'signed-out' }
  | { state: 'signed-in'; user: User; session: Session };

/** Follows the signed-in user. Supabase restores and refreshes the session by itself. */
export function useAuth(): AuthState {
  const [auth, setAuth] = useState<AuthState>(supabase ? { state: 'loading' } : { state: 'unavailable' });

  useEffect(() => {
    if (!supabase) return;
    let alive = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setAuth(data.session ? { state: 'signed-in', user: data.session.user, session: data.session } : { state: 'signed-out' });
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuth(session ? { state: 'signed-in', user: session.user, session } : { state: 'signed-out' });
    });
    return () => {
      alive = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return auth;
}

export const displayName = (user: User): string =>
  (user.user_metadata?.['full_name'] as string | undefined) ?? user.email ?? 'Signed in';

export async function signOut(): Promise<void> {
  await supabase?.auth.signOut();
}
